import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Camera,
  Upload,
  Scan,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Sparkles,
  FileText,
  AlertCircle,
  Eye,
  X
} from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PassportScanner = ({ onDataExtracted, onClose }) => {
  const [step, setStep] = useState("upload"); // upload, scanning, preview, error
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato no soportado. Usa JPG, PNG o WEBP.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo es muy grande. Máximo 10MB.");
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  const handleScan = async () => {
    if (!selectedFile) return;

    setStep("scanning");
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(`${API}/ocr/passport`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (response.data.success) {
        setExtractedData(response.data);
        setStep("preview");
        toast.success("¡Datos extraídos exitosamente!");
      } else {
        throw new Error("No se pudieron extraer los datos");
      }
    } catch (error) {
      console.error("OCR Error:", error);
      setError(error.response?.data?.detail || "Error al analizar el pasaporte");
      setStep("error");
      toast.error("Error al escanear el pasaporte");
    }
  };

  const handleConfirm = () => {
    if (extractedData?.ds160_fields) {
      onDataExtracted(extractedData.ds160_fields);
      toast.success("Datos aplicados al formulario");
    }
  };

  const handleRetry = () => {
    setStep("upload");
    setSelectedFile(null);
    setPreviewUrl(null);
    setExtractedData(null);
    setError(null);
  };

  const formatFieldName = (key) => {
    const names = {
      surnames: "Apellidos",
      given_names: "Nombres",
      sex: "Sexo",
      date_of_birth: "Fecha de Nacimiento",
      nationality: "Nacionalidad",
      passport_number: "Número de Pasaporte",
      passport_issue_date: "Fecha de Expedición",
      passport_expiry_date: "Fecha de Vencimiento",
      passport_issuing_country: "País de Expedición",
      city_of_birth: "Ciudad de Nacimiento",
      country_of_birth: "País de Nacimiento"
    };
    return names[key] || key;
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scan className="w-5 h-5 text-emerald-600" />
            Escanear Pasaporte
          </DialogTitle>
          <DialogDescription>
            Sube una foto de tu pasaporte para auto-llenar el formulario DS-160
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {/* Upload Step */}
          {step === "upload" && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {!previewUrl ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-navy-200 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-all"
                >
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Camera className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="text-navy-900 font-medium mb-1">
                    Haz clic para subir foto del pasaporte
                  </p>
                  <p className="text-sm text-navy-500">
                    JPG, PNG o WEBP (máx. 10MB)
                  </p>
                </div>
              ) : (
                <div className="relative">
                  <img
                    src={previewUrl}
                    alt="Passport preview"
                    className="w-full h-48 object-contain bg-navy-50 rounded-lg"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="absolute top-2 right-2"
                    onClick={handleRetry}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />

              {previewUrl && (
                <Button
                  onClick={handleScan}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analizar Pasaporte con IA
                </Button>
              )}

              <div className="bg-blue-50 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                <p className="text-xs text-blue-700">
                  Asegúrate de que la foto esté clara y muestre la página de datos del pasaporte completa.
                </p>
              </div>
            </motion.div>
          )}

          {/* Scanning Step */}
          {step === "scanning" && (
            <motion.div
              key="scanning"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="py-12 text-center"
            >
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                <Scan className="w-10 h-10 text-emerald-600" />
                <div className="absolute inset-0 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2">
                Analizando pasaporte...
              </h3>
              <p className="text-navy-500 text-sm">
                La IA está extrayendo los datos. Esto puede tomar unos segundos.
              </p>
            </motion.div>
          )}

          {/* Preview Step */}
          {step === "preview" && extractedData && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-2 text-emerald-600 mb-2">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Datos extraídos exitosamente</span>
              </div>

              <div className="bg-navy-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                <div className="space-y-2">
                  {Object.entries(extractedData.ds160_fields || {}).map(([key, value]) => (
                    value && (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-navy-500">{formatFieldName(key)}:</span>
                        <span className="font-medium text-navy-900">{value}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={handleRetry} className="flex-1">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Escanear Otro
                </Button>
                <Button onClick={handleConfirm} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Aplicar Datos
                </Button>
              </div>
            </motion.div>
          )}

          {/* Error Step */}
          {step === "error" && (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="py-8 text-center"
            >
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2">
                Error al analizar
              </h3>
              <p className="text-navy-500 text-sm mb-4">
                {error || "No se pudo extraer la información del pasaporte"}
              </p>
              <Button onClick={handleRetry} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Intentar de nuevo
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default PassportScanner;
